// RCRA Biennial (Hazardous Waste) Report GM1 form code lists (EPA RCRAInfo Industry Application).
//
// IMPORTANT: the official "RCRAInfo File Specification Guide" (which defines the GM1/GM2/GM4/GM5
// flat-file layout used elsewhere in this app) explicitly states that its own document does NOT
// enumerate these nationally-defined code lists - they live in the separate "RCRA Subtitle C
// Reporting, Instructions and Forms" booklet. The entries below marked "confirmed" were directly
// referenced (code + business rule) in the File Specification Guide itself (Dec 2024) and can be
// trusted; the rest are commonly-seen codes included as a convenience starting point and should be
// verified against the current RCRAInfo lookup tables (or your state program) before relying on
// them for a real submission. An explicit "other" option is always included so a facility can enter
// any code not listed here.
export const OTHER_CODE_SENTINEL = '__other__';

export interface BiennialReportCode {
  code: string;
  description: string;
  /** True for codes whose exact meaning was directly confirmed by the EPA File Specification
   * Guide's own business-rule text, as opposed to general reference knowledge. */
  confirmed?: boolean;
}

// GM-1-D "Source Code" - describes where/how the reported waste stream originated.
export const SOURCE_CODES: readonly BiennialReportCode[] = [
  { code: 'G01', description: 'Dip, flush, or spray rinsing' },
  { code: 'G02', description: 'Stripping and acid or caustic cleaning' },
  { code: 'G03', description: 'Plating and phosphating (electro- or non-electroplating or phosphating)' },
  { code: 'G04', description: 'Etching (using caustics or other methods to remove layers or partial layers)' },
  {
    code: 'G05',
    description:
      'Metal forming and treatment (picking, heat treating, punching, bending, annealing, grinding, hardening)',
  },
  { code: 'G06', description: 'Painting and coating (manufacturing, building, or maintenance)' },
  {
    code: 'G07',
    description: 'Product or by-product processing (direct flow of wastes from chemical manufacturing or processing)',
  },
  { code: 'G08', description: 'Removal of spent process liquids or catalysts' },
  { code: 'G09', description: 'Other production or service-related process waste (specify in comments)' },
  { code: 'G11', description: 'Discarding off-specification, out-of-date, or unused chemicals or products' },
  { code: 'G12', description: 'Lagoon or sediment dragout and leachate collection' },
  {
    code: 'G13',
    description: 'Cleaning out process equipment (periodic sludge or residual removal from enclosed process equipment)',
  },
  { code: 'G14', description: 'Removal of tank sludge, sediments, or slag' },
  { code: 'G15', description: 'Process equipment change-out or discontinuation of equipment use' },
  { code: 'G16', description: 'Oil changes and filter or battery replacement' },
  {
    code: 'G17',
    description:
      'Laboratory waste from an eligible academic entity opted into the Subpart K (40 CFR 262 Subpart K) alternative requirements',
    confirmed: true,
  },
  { code: 'G19', description: 'Other one-time or intermittent process (specify in comments)' },
  {
    code: 'G21',
    description:
      'Air pollution control devices (e.g., baghouse dust, ash, stack scrubbers, precipitators, vapor collection)',
  },
  { code: 'G22', description: 'Laboratory analytical wastes' },
  {
    code: 'G23',
    description: 'Wastewater treatment (e.g., sludge, filter cake, including wastes from treatment before discharge)',
  },
  { code: 'G24', description: 'Solvent or product distillation as part of a production process' },
  {
    code: 'G25',
    description: 'Treatment, disposal, or recycling of hazardous wastes (report related management method code)',
    confirmed: true,
  },
  { code: 'G26', description: 'Leachate collection (from landfill operations or other land units)' },
  { code: 'G27', description: 'Treatment or recovery of universal waste' },
  { code: 'G31', description: 'Accidental contamination of products, materials, or containers (other than G11)' },
  { code: 'G32', description: 'Cleanup of spill residues (infrequent, not routine)' },
  { code: 'G33', description: 'Leak collection and floor sweeping (on-going, routine)' },
  { code: 'G39', description: 'Other cleanup of current contamination (specify in comments)' },
  {
    code: 'G61',
    description: 'Waste received from off-site for storage/bulking and transfer off-site for treatment or disposal',
    confirmed: true,
  },
  { code: 'G41', description: 'Closure of hazardous waste management unit under RCRA' },
  { code: 'G42', description: 'Corrective action at a solid waste management unit under RCRA' },
  { code: 'G43', description: 'Remedial action or emergency response under Superfund' },
  { code: 'G44', description: 'Cleanup under State or voluntary program' },
  { code: 'G45', description: 'Cleanup of underground storage tanks' },
  { code: 'G49', description: 'Other remediation (specify in comments)' },
  { code: 'G51', description: 'Hazardous waste received by an LQG from VSQGs under the control of the same person' },
  {
    code: 'G62',
    description: 'Waste imported from a foreign country (Country Code required)',
    confirmed: true,
  },
  {
    code: 'G76',
    description:
      "Subpart P Reverse Distributor pharmaceutical waste (requires 'Reverse Distributor' = Y on the Site ID form)",
    confirmed: true,
  },
  {
    code: 'G77',
    description:
      'Airbag waste received from airbag waste handlers (prior to arrival at the airbag collection facility or designated facility)',
    confirmed: true,
  },
];

// GM-1-E "Waste Form Code" - describes the physical form of the reported waste.
export const FORM_CODES: readonly BiennialReportCode[] = [
  { code: 'W001', description: 'Lab packs from any source not containing acute hazardous waste' },
  { code: 'W002', description: 'Contaminated debris' },
  { code: 'W004', description: 'Lab packs from any source containing acute hazardous waste' },
  { code: 'W005', description: 'Waste pharmaceuticals managed as hazardous waste', confirmed: true },
  { code: 'W006', description: 'Airbag waste (airbag modules or inflators managed as hazardous waste)' },
  { code: 'W101', description: 'Very dilute aqueous waste containing more than 99% water' },
  { code: 'W103', description: 'Spent concentrated acid (5% or more)' },
  { code: 'W105', description: 'Acidic aqueous wastes less than 5%' },
  { code: 'W107', description: 'Aqueous waste containing cyanides (generally caustic)' },
  { code: 'W110', description: 'Caustic aqueous waste without cyanides' },
  { code: 'W113', description: 'Other aqueous waste or wastewaters (not sludge)' },
  { code: 'W117', description: 'Waste liquid mercury (metallic)' },
  { code: 'W119', description: 'Other inorganic liquid (specify in comments)' },
  { code: 'W200', description: 'Still bottoms in liquid form (fluid but not sludge)' },
  { code: 'W202', description: 'Concentrated halogenated solvent' },
  { code: 'W203', description: 'Concentrated non-halogenated solvent' },
  { code: 'W204', description: 'Concentrated halogenated/non-halogenated solvent mixture' },
  { code: 'W205', description: 'Oil-water emulsion or mixture (fluid but not sludge)' },
  { code: 'W206', description: 'Waste oil managed as hazardous waste' },
  { code: 'W209', description: 'Paint, ink, lacquer, or varnish (fluid, not dried out or sludge)' },
  { code: 'W210', description: 'Reactive or polymerizable organic liquids and adhesives (fluid but not sludge)' },
  { code: 'W211', description: 'Paint thinner or petroleum distillates' },
  { code: 'W219', description: 'Other organic liquid (specify in comments)' },
  { code: 'W301', description: 'Contaminated soil (includes spill cleanup, demolition, or remediation)' },
  { code: 'W309', description: 'Batteries, battery parts, cores, casings (lead-acid or other types)' },
  { code: 'W310', description: 'Filters, solid adsorbents, ion exchange resins, and spent carbon' },
  { code: 'W320', description: 'Electrical devices (lamps, fluorescent lamps, thermostats containing mercury, CRTs)' },
  { code: 'W512', description: 'Sediment or lagoon dragout, drilling or other muds (wet or muddy soil)' },
  { code: 'W801', description: 'Compressed gases of any type' },
  { code: 'W303', description: 'Ash (from any type of burning of hazardous waste)' },
  { code: 'W304', description: 'Slag, dross, and other solid thermal residues' },
  { code: 'W307', description: 'Metal scale, filings, and scrap (including metal drums)' },
  { code: 'W312', description: 'Cyanide or metal cyanide bearing solids, salts, or chemicals' },
  { code: 'W316', description: 'Metal chemicals not containing cyanides' },
  { code: 'W319', description: 'Other inorganic solids (specify in comments)' },
  { code: 'W401', description: 'Pesticide solids (used or discarded, not contaminated soils)' },
  { code: 'W403', description: 'Solid resins, plastics, or polymerized organics' },
  { code: 'W405', description: 'Explosives or reactive organic solids' },
  { code: 'W406', description: 'Dried paint (paint chips, filters, sludges, or other)' },
  { code: 'W409', description: 'Other organic solids (specify in comments)' },
  { code: 'W501', description: 'Lime and/or metal hydroxide sludges and solids with no cyanides' },
  { code: 'W503', description: 'Gypsum sludges from wastewater treatment or air pollution control' },
  { code: 'W504', description: 'Other sludges from wastewater treatment or air pollution control' },
  { code: 'W505', description: 'Metal-bearing sludges (including plating sludge) not containing cyanides' },
  { code: 'W506', description: 'Cyanide-bearing sludges (not contaminated soils)' },
  { code: 'W519', description: 'Other inorganic sludges (specify in comments)' },
  { code: 'W603', description: 'Oily sludge (not contaminated muds)' },
  { code: 'W604', description: 'Paint or ink sludge (still bottoms or thinners sludge form)' },
  { code: 'W606', description: 'Resins, tars, polymer, or tarry sludge' },
  { code: 'W609', description: 'Other organic sludge (specify in comments)' },
];

// GM-1-G "Waste Minimization Indicator" - whether this waste stream was affected by the
// facility's waste minimization efforts during the reporting cycle.
export const WASTE_MIN_CODES: readonly BiennialReportCode[] = [
  { code: 'A', description: 'Continued initiatives to reduce the quantity and/or toxicity of this waste' },
  { code: 'B', description: 'Continued initiatives to recycle this waste on-site or off-site' },
  { code: 'C', description: 'Implemented new initiatives to reduce the quantity and/or toxicity of this waste' },
  { code: 'D', description: 'Implemented new initiatives to recycle this waste on-site or off-site' },
  { code: 'N', description: 'Waste minimization efforts were economically or technically impracticable' },
  { code: 'X', description: 'No waste minimization efforts were implemented for this waste' },
];

// GM2-C / GM4-C / GM5 "Management Method Code" - how the waste was or will be managed.
// H-codes are the primary nationally-defined treatment/disposal/recycling codes; S-codes were
// added in the 2025 cycle as replacements for H141 (stored/bulked and transferred to another
// site) with more specific subcategories. The H141 code itself is deprecated after 2025.
// Source: EPA File Specification Guide Dec 2024 business rules + RCRA Subtitle C Reporting
// Instructions and Forms.
export const MANAGEMENT_METHOD_CODES: readonly BiennialReportCode[] = [
  // Recovery
  { code: 'H010', description: 'Metals recovery including retorting, smelting, chemical, etc.' },
  { code: 'H011', description: 'Mercury recovery' },
  { code: 'H015', description: 'Deployment/deactivation of airbag waste followed by metals recovery' },
  { code: 'H020', description: 'Solvents recovery (distillation, extraction, etc.)' },
  { code: 'H039', description: 'Other recovery or reclamation (acid regeneration, organics recovery, etc.)' },
  // Incineration / thermal
  { code: 'H040', description: 'Incineration / thermal destruction other than use as fuel' },
  { code: 'H041', description: 'Open burning / open detonation (Subpart X, process code X01)' },
  { code: 'H042', description: 'Thermal desorption (organic contaminants from soil/sludge/sediment)' },
  // Energy recovery / fuel
  { code: 'H050', description: 'Energy recovery at this site - used as fuel' },
  { code: 'H061', description: 'Fuel blending prior to energy recovery at another site' },
  // Chemical / biological / physical treatment
  { code: 'H070', description: 'Chemical treatment (reduction, destruction, oxidation, precipitation)' },
  { code: 'H081', description: 'Biological treatment' },
  { code: 'H090', description: 'Polymerization (LDR standard as treatment method)' },
  { code: 'H100', description: 'Physical treatment only (adsorption, absorption, separation, stripping, dewatering)' },
  // Stabilization / immobilization
  {
    code: 'H110',
    description: 'Stabilization prior to land disposal at another site (encapsulation, stabilization, fixation)',
  },
  { code: 'H113', description: 'Stabilization to remove hazardous waste characteristics or achieve delisting levels' },
  { code: 'H120', description: 'Combination of chemical, biological, and/or physical treatment' },
  { code: 'H121', description: 'Neutralization only (no other treatment)' },
  { code: 'H122', description: 'Evaporation (as the major component of treatment)' },
  // Land disposal
  { code: 'H129', description: 'Other treatment that does not include on-site disposal' },
  { code: 'H130', description: 'Surface impoundment that will be closed as a landfill' },
  { code: 'H131', description: 'Land treatment or application (with prior treatment and/or stabilization)' },
  { code: 'H132', description: 'Landfill (with prior treatment and/or stabilization)' },
  { code: 'H134', description: 'Deepwell or underground injection (with or without treatment)' },
  { code: 'H136', description: 'Discharge to sewer/POTW (with prior storage)' },
  { code: 'H137', description: 'Discharge with NPDES permit (with prior storage)' },
  // Transfer / storage (S-codes replace H141 from 2025 cycle)
  {
    code: 'H141',
    description: 'Stored/bulked and transferred without reclamation, recovery, destruction, treatment, or disposal',
    confirmed: true,
  },
  // S-codes (2025 cycle, confirmed in File Spec Guide Dec 2024)
  {
    code: 'S010',
    description: 'Transfer to another site — metals recovery (retorting, smelting, chemical)',
    confirmed: true,
  },
  { code: 'S011', description: 'Transfer to another site — mercury recovery', confirmed: true },
  {
    code: 'S015',
    description: 'Transfer to another site — airbag waste deployment/deactivation then metals recovery',
    confirmed: true,
  },
  { code: 'S020', description: 'Transfer to another site — solvents recovery', confirmed: true },
  {
    code: 'S039',
    description: 'Transfer to another site — other recovery/reclamation (acid regeneration, organics, etc.)',
    confirmed: true,
  },
  {
    code: 'S040',
    description: 'Transfer to another site — incineration / thermal destruction other than fuel use',
    confirmed: true,
  },
  {
    code: 'S041',
    description: 'Transfer to another site — open burning/open detonation (Subpart X, X01)',
    confirmed: true,
  },
  { code: 'S042', description: 'Transfer to another site — thermal desorption', confirmed: true },
  {
    code: 'S050',
    description: 'Transfer to another site — energy recovery (incl. on-site fuel blending)',
    confirmed: true,
  },
  { code: 'S061', description: 'Transfer to another site — fuel blending prior to energy recovery', confirmed: true },
  {
    code: 'S070',
    description: 'Transfer to another site — chemical treatment (reduction/destruction/oxidation/precipitation)',
    confirmed: true,
  },
  { code: 'S081', description: 'Transfer to another site — biological treatment', confirmed: true },
  { code: 'S090', description: 'Transfer to another site — polymerization (LDR standard)', confirmed: true },
  {
    code: 'S100',
    description:
      'Transfer to another site — physical treatment only (adsorption/absorption/separation/stripping/dewatering)',
    confirmed: true,
  },
  { code: 'S110', description: 'Transfer to another site — stabilization prior to land disposal', confirmed: true },
  {
    code: 'S113',
    description: 'Transfer to another site — stabilization to remove characteristics or achieve delisting levels',
    confirmed: true,
  },
  {
    code: 'S120',
    description: 'Transfer to another site — combination of chemical, biological, and/or physical treatment',
    confirmed: true,
  },
  { code: 'S121', description: 'Transfer to another site — neutralization only', confirmed: true },
  { code: 'S122', description: 'Transfer to another site — evaporation', confirmed: true },
  { code: 'S129', description: 'Transfer to another site — other treatment (no on-site disposal)', confirmed: true },
  { code: 'S130', description: 'Transfer to another site — surface impoundment closed as landfill', confirmed: true },
  { code: 'S131', description: 'Transfer to another site — land treatment or application', confirmed: true },
  {
    code: 'S132',
    description: 'Transfer to another site — landfill (with prior treatment/stabilization)',
    confirmed: true,
  },
  { code: 'S134', description: 'Transfer to another site — deepwell or underground injection', confirmed: true },
];
